/**
 * Created by workaxis www.workaxis.in
 *
 * SubscriptionCheck — renders a contextual alert banner based on the canonical
 * subscription_state returned by the profile API. Also exports the
 * useSubscriptionLock() hook for paid-user disabled states.
 *
 * States handled:
 *   "active"       — no banner shown
 *   "cancelling"   — yellow warning: plan cancelled, access until [end_date]
 *   "grace_period" — orange warning: plan ended, grace window closes [grace_ends_at]
 *   "expired"      — red error: locked — paid actions are disabled
 *   "none" / null  — no banner shown (free user, not our concern here)
 */
import React, {memo, useMemo} from "react";
import "./style.css";
import {useSelector} from "react-redux";
import {profileState} from "../../redux/profile/profileReducer";
import Alert from "../Alert/Alert";
import {adminRole, ownerRole, staffRole} from "../../utils/constants";
import {getSubscriptionState} from "../Subscription/Children/subscriptionDisplayUtils";

const PAID_ROLES = [staffRole, adminRole, ownerRole];

/**
 * Returns the normalized subscription_state object from the profile.
 */
export function useSubscriptionState() {
    const {profile} = useSelector(profileState);
    return useMemo(() => getSubscriptionState(profile?.data || {}), [profile?.data]);
}

/**
 * Returns true when the grace period has ended and paid features must be locked.
 * Use this hook in any component that renders a paid-only action button or section.
 *
 * Example:
 *   const locked = useSubscriptionLock();
 *   <Button disabled={locked}>Upgrade plan</Button>
 */
export function useSubscriptionLock() {
    const state = useSubscriptionState();
    return state?.state === "expired";
}

/**
 * Formats a YYYY-MM-DD string into a readable date, e.g. "14 Apr 2026".
 */
function formatDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
}

export const SubscriptionCheck = memo(() => {
    const {profile} = useSelector(profileState);
    const subState = useSubscriptionState();

    const myRole = profile?.data?.userorgrolemapping?.role?.name;
    const isOrgMember = PAID_ROLES.includes(myRole);

    // Only show alerts to org members (owner / admin / staff)
    if (!isOrgMember || !subState) return null;

    const {state, period = {}, grace = {}} = subState;
    const isOwner = myRole === ownerRole;

    if (state === "cancelling") {
        const endStr = formatDate(period.end_date);
        return (
            <div className={"subscription-alert-container"}>
                <Alert
                    type={"warning"}
                    title={"Plan cancellation scheduled"}
                    description={
                        isOwner
                            ? `Your subscription has been cancelled. You and your team retain full access until ${endStr}. Renew before then to avoid any interruption.`
                            : `Your organisation's subscription has been cancelled. Full access continues until ${endStr}. Contact your owner to renew.`
                    }
                />
            </div>
        );
    }

    if (state === "grace_period") {
        const graceStr = formatDate(grace.ends_date);
        const daysLeft = typeof grace.days_remaining === "number" ? grace.days_remaining : "?";
        return (
            <div className={"subscription-alert-container"}>
                <Alert
                    type={"error"}
                    title={`Subscription ended — grace period (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)`}
                    description={
                        isOwner
                            ? `Your subscription has ended. You have until ${graceStr} to renew before paid features are locked for your entire organisation.`
                            : `Your organisation's subscription has ended. The owner has until ${graceStr} to renew. Contact them to avoid losing access to paid features.`
                    }
                />
            </div>
        );
    }

    if (state === "expired") {
        return (
            <div className={"subscription-alert-container subscription-alert-locked"}>
                <Alert
                    type={"error"}
                    title={"Paid features locked — subscription expired"}
                    description={
                        isOwner
                            ? "Your subscription and grace period have ended. Paid features are currently disabled. Renew your plan to restore full access for you and your team."
                            : "Your organisation's subscription has expired and paid features are currently locked. Please contact your owner to renew the plan."
                    }
                />
            </div>
        );
    }

    // "active" and "none" — no banner needed
    return null;
});
